import { AddressesAddressDto } from "@springtree/eva-services-core";

import Logger from "~/services/logger/logger";
import $fetch from "~/utils/fetch-utils";

import invariant from "./invariant";

type WoosmapLocalityField =
  | "name"
  | "administrative_area_level_0"
  | "administrative_area_level_1"
  | "administrative_area_level_2"
  | "postal_codes"
  | "postal_code"
  | "postal_town"
  | "locality"
  | "iata_code"
  | "route"
  | "street_number"
  | "premise"
  | "building"
  | "sub_building"
  | "organisation"
  | "post_box"
  | "district"
  | "county"
  | "country"
  | "state";

export type WooosmapGeometry = {
  location: {
    lat: number;
    lng: number;
  };
  viewport?: {
    northeast: {
      lat: number;
      lng: number;
    };
    southwest: {
      lat: number;
      lng: number;
    };
  };
};

export type WoosmapSuggestion = {
  address_components: { long_name: string; short_name: string; types: WoosmapLocalityField[] }[];
  name?: string;
  formatted_address: string;
  public_id: string;
  types?: WoosmapLocalityField[];
  geometry: WooosmapGeometry;
};

type WoosmapMappingRule = {
  field: WoosmapLocalityField;
  component: "long_name" | "short_name";
  fallback?: boolean;
};

type WoosmapLocalitiesMappingCountry = Partial<Record<keyof Omit<AddressesAddressDto, "ID">, WoosmapMappingRule[]>>;

const defaultLocalitiesMapping = {
  Address1: [{ field: "route", component: "long_name" }],
  HouseNumber: [{ field: "street_number", component: "long_name" }],
  City: [{ field: "locality", component: "long_name" }],
  District: [{ field: "county", component: "short_name" }],
  State: [{ field: "state", component: "short_name" }],
  ZipCode: [
    { field: "postal_code", component: "short_name" },
    { field: "postal_codes", component: "short_name" },
  ],
} satisfies WoosmapLocalitiesMappingCountry;

const middleEastBaseLocalitiesMapping = {
  Address1: [{ field: "route", component: "long_name" }],
  City: [{ field: "locality", component: "long_name" }],
  District: [{ field: "district", component: "long_name" }],
  HouseNumber: [{ field: "street_number", component: "long_name" }],
  State: [{ field: "locality", component: "long_name" }],
  ZipCode: [{ field: "postal_codes", component: "long_name" }],
} satisfies WoosmapLocalitiesMappingCountry;

const WoosmapLocalitiesMapping = {
  AE: {
    ...middleEastBaseLocalitiesMapping,
  },
  QA: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  KW: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  BH: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  OM: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  JO: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  US: {
    Address1: [
      { field: "street_number", component: "short_name" },
      { field: "route", component: "long_name" },
    ],
    City: [{ field: "locality", component: "long_name" }],
    District: [{ field: "locality", component: "long_name" }],
    State: [{ field: "state", component: "short_name" }],
    ZipCode: [{ field: "postal_codes", component: "short_name" }],
  },
  FR: {
    ...defaultLocalitiesMapping,
    City: [
      { field: "locality", component: "long_name" },
      { field: "postal_town", component: "long_name" },
    ],
    District: [
      { field: "county", component: "long_name" },
      { field: "administrative_area_level_1", component: "long_name" },
    ],
  },
  ES: {
    ...defaultLocalitiesMapping,
    State: [{ field: "state", component: "short_name" }],
  },
  GB: {
    ...defaultLocalitiesMapping,
    HouseNumber: [
      { field: "street_number", component: "long_name" },
      { field: "building", component: "long_name", fallback: true },
    ],
    Address2: [{ field: "sub_building", component: "long_name" }],
    City: [
      { field: "postal_town", component: "long_name" },
      { field: "locality", component: "long_name", fallback: true },
    ],
  },
} satisfies Record<string, WoosmapLocalitiesMappingCountry>;

const woosmapUtils = {
  mapSuggestionToAddressDto(suggestion: WoosmapSuggestion, countryID: string): Omit<AddressesAddressDto, "ID"> {
    let address: Omit<AddressesAddressDto, "ID"> = { CountryID: "" };
    const countryMapping =
      WoosmapLocalitiesMapping[countryID as keyof typeof WoosmapLocalitiesMapping] || defaultLocalitiesMapping;

    const getComponentValue = (rule: WoosmapMappingRule) =>
      suggestion.address_components.find((ac) => ac.types.includes(rule.field))?.[rule.component];

    for (const [dtoField, rules] of Object.entries(countryMapping) as [string, WoosmapMappingRule[]][]) {
      const primaryValues = rules
        .filter((rule) => !rule.fallback)
        .map(getComponentValue)
        .filter(Boolean);
      const values =
        primaryValues.length > 0
          ? primaryValues
          : rules
              .filter((rule) => rule.fallback)
              .map(getComponentValue)
              .filter(Boolean);

      if (values.length > 0) {
        // @ts-expect-error - Dynamic access to address field
        address[dtoField] = values.join(" ");
      }
    }

    return address;
  },

  async getAddressDetails(publicId: string, language?: string) {
    const params: { [key: string]: any } = {
      key: process.env.NEXT_PUBLIC_WOOSMAP_API_KEY!,
      public_id: publicId,
    };

    if (language) {
      params.language = language;
    }

    return await $fetch(`https://api.woosmap.com/address/details/json`, { searchParams: params })
      .json()
      .catch(() => {
        Logger.instance.error(`autocomplete: couldn't get address detail for id ${publicId}`);
        return null;
      });
  },

  async getAddressState(address: string, countryID: string, language?: string) {
    try {
      const params: { [key: string]: any } = {
        key: process.env.NEXT_PUBLIC_WOOSMAP_API_KEY!,
        components: `country:${countryID}`,
        fields: "address_components",
        address,
      };

      if (language) {
        params.language = language;
      }

      const json = await $fetch(`https://api.woosmap.com/localities/geocode`, { searchParams: params }).json<{
        results: any[];
      }>();

      invariant(Array.isArray(json.results), `Got result from Woosmap but results is not an array.`);

      if (json.results.length === 0) {
        Logger.instance.warn("Could not find state for address: empty results");
        return null;
      }

      const components = json.results[0].address_components;

      invariant(Array.isArray(components), `Got result from Woosmap but address_components is not an array.`);

      if (components.length === 0) {
        Logger.instance.warn("Could not find state for address: empty address_components");
        return null;
      }

      const state = components.find((c) => c.types.includes("state"));

      if (!state) {
        return null;
      }

      return state.short_name as string;
    } catch (err) {
      Logger.instance.error("Could not find state for address:", err);
      return null;
    }
  },
};

export default woosmapUtils;
